import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  IDeleteOnuDto,
  IFullStatusOnut,
  IOdbModel,
  IOnuAuthorizeCreate,
  IOnuDetails,
  IOnuModel,
  IOnusType,
  IPlanSpeedModel,
  IRunningConfigOnu,
} from '@modules/clients/services/olt/models/onus.model';
import { DatailsSmartOltConfigurationsComponent } from '@sharedComponents/modals/datails-smart-olt-configurations/datails-smart-olt-configurations.component';
import {
  ILiveStatusResponse,
  LiveStatuSmartOltModalComponent,
} from '@sharedComponents/modals/live-statu-smart-olt-modal/live-statu-smart-olt-modal.component';
import { ShowRunningConfigOnuComponent } from '@sharedComponents/modals/show-running-config-onu/show-running-config-onu.component';
import { StatusInfoOnuComponent } from '@sharedComponents/modals/status-info-onu/status-info-onu.component';
import {
  ONU_DIALOG_BREAKPOINTS,
  ONU_DIALOG_HEIGHT,
  ONU_DIALOG_WIDTH,
} from '@sharedComponents/smart-olt-onu/onu-dialog-size';
import { IKeyValueGeneric } from '@sharedModels/key-value-generic.model';
import { IResponse, IResponsePagination } from '@sharedModels/response-pagination.model';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom } from 'rxjs';
import { GlobalStateService } from 'src/app/shared/services/global-state/global-state.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class OltService {
  constructor(
    private readonly http: HttpClient,
    public readonly dialogService: DialogService,
    private readonly globalStateService: GlobalStateService,
  ) {}

  /**
   * getOnusforConfiguration
   */
  public async getOnusforConfiguration(companyOwner: number): Promise<IResponsePagination<IOnuModel>> {
    return await firstValueFrom(
      this.http.get<IResponsePagination<IOnuModel>>(`${environment.apiUrl}/smart-olt/all-unconfigured`, {
        params: { companyOwnerId: companyOwner },
      }),
    );
  }

  /**
   * getOnusforConfigurationById
   */
  public async getOnusforConfigurationById(oltId: string): Promise<IResponsePagination<any>> {
    return await firstValueFrom(
      this.http.get<IResponsePagination<any>>(`${environment.apiUrl}/smart-olt/all-unconfigured/${oltId}`),
    );
  }

  /**
   * getVlansByOltId
   */
  public async getVlansByOltId(oltId: string, companyOwner: number): Promise<IResponse<any>> {
    return await firstValueFrom(
      this.http.get<IResponse<any>>(`${environment.apiUrl}/smart-olt/vlans/${oltId}`, {
        params: { companyOwnerId: companyOwner },
      }),
    );
  }

  /**
   * getPlanSpeed
   */
  public async getPlanSpeed() {
    return await firstValueFrom(
      this.http.get<IResponsePagination<IPlanSpeedModel>>(`${environment.apiUrl}/smart-olt/plans-speed`),
    );
  }

  /**
   * getAllOnusType
   */
  public async getAllOnusType(companyOwnerId: number) {
    return await firstValueFrom(
      this.http.get<IResponsePagination<IOnusType>>(`${environment.apiUrl}/smart-olt/all-onus-type`, {
        params: { companyOwnerId },
      }),
    );
  }

  /**
   * getZones
   */
  public async getZones(companyOwnerId: number) {
    return await firstValueFrom(
      this.http.get<IResponsePagination<IKeyValueGeneric>>(`${environment.apiUrl}/smart-olt/zones`, {
        params: { companyOwnerId },
      }),
    );
  }

  /**
   * getOdbs
   */
  public async getOdbs() {
    return await firstValueFrom(
      this.http.get<IResponsePagination<IOdbModel>>(`${environment.apiUrl}/smart-olt/all-odbs`),
    );
  }

  /**
   * authorizeOnu
   */
  public async authorizeOnu(body: IOnuAuthorizeCreate) {
    return await firstValueFrom(this.http.post(`${environment.apiUrl}/smart-olt/authorize-onu`, body));
  }

  /**
   * getOnuDeatilsBySn
   */
  public async getOnuDeatilsBySn(sn: string, companyOwnerId: number) {
    return await firstValueFrom(
      this.http.get<IResponse<IOnuDetails>>(`${environment.apiUrl}/smart-olt/get-onus-details-by-sn/${sn}`, {
        params: { companyOwnerId },
      }),
    );
  }

  /**
   * @name getOnuSignalsBySn
   * @description get onu signals by sn
   */
  public async getOnuSignalsBySn(sn: string, companyOwnerId: number) {
    return await firstValueFrom(
      this.http.get<
        IResponse<{
          onu_signal: string;
          onu_signal_value: string;
          onu_signal_1310: string;
          onu_signal_1490: string;
        }>
      >(`${environment.apiUrl}/smart-olt/get-onu-signals-by-sn/${sn}`, {
        params: { companyOwnerId },
      }),
    );
  }

  /**
   * @name getOnuStatusBySn
   * @description get onu signals by sn
   */
  public async getOnuStatusBySn(sn: string, companyOwnerId: number) {
    return await firstValueFrom(
      this.http.get<IResponse<string>>(`${environment.apiUrl}/smart-olt/get-onu-status-by-sn/${sn}`, {
        params: { companyOwnerId },
      }),
    );
  }

  /**
   * deleteOnu
   */
  public async deleteOnu(body: IDeleteOnuDto) {
    return await firstValueFrom(
      this.http.post(`${environment.apiUrl}/smart-olt/delete-onu`, body, {
        params: { companyOwnerId: body.companyOwner },
      }),
    );
  }

  /**
   * rebootOnu
   */
  public async rebootOnu(externalId: string, companyOwnerId: number) {
    return await firstValueFrom(
      this.http.get(`${environment.apiUrl}/smart-olt/reboot-onu/${externalId}`, {
        params: { companyOwnerId },
      }),
    );
  }

  /**
   * restoreOnu
   */
  public async restoreOnu(externalId: string, companyOwnerId: number) {
    return await firstValueFrom(
      this.http.get(`${environment.apiUrl}/smart-olt/restore-onu/${externalId}`, {
        params: { companyOwnerId },
      }),
    );
  }
  /**
   * resyncOnu
   */
  public async resyncOnu(externalId: string, companyOwnerId: number) {
    return await firstValueFrom(
      this.http.get(`${environment.apiUrl}/smart-olt/resync-onu/${externalId}`, {
        params: { companyOwnerId },
      }),
    );
  }

  /**
   * disableOnu
   */
  public async disableOnu(body: { externalId: string }, companyOwnerId: number) {
    return await firstValueFrom(
      this.http.post(`${environment.apiUrl}/smart-olt/disable-onu`, body, {
        params: { companyOwnerId },
      }),
    );
  }

  /**
   * enableOnu
   */
  public async enableOnu(body: { externalId: string }, companyOwnerId: number) {
    return await firstValueFrom(
      this.http.post(`${environment.apiUrl}/smart-olt/enable-onu`, body, {
        params: { companyOwnerId },
      }),
    );
  }

  /**
   * getFullInfoStatusOnu
   */
  public async getFullInfoStatusOnu(sn: string, companyOwnerId: number) {
    return await firstValueFrom(
      this.http.get<IResponse<IFullStatusOnut>>(`${environment.apiUrl}/smart-olt/full-info-status/${sn}`, {
        params: { companyOwnerId },
      }),
    );
  }

  /**
   * getRunningConfig
   */
  public async getRunningConfig(sn: string, companyOwnerId: number) {
    return await firstValueFrom(
      this.http.get<IResponse<IRunningConfigOnu>>(`${environment.apiUrl}/smart-olt/running-config/${sn}`, {
        params: { companyOwnerId },
      }),
    );
  }

  /**
   * showDetailSmartOlt
   */
  public async showDetailSmartOlt(sn: string, contractID: number, companyOwner: number): Promise<any> {
    // el modal carga por su cuenta la pantalla completa de la ONU desde SmartOLT
    const ref: DynamicDialogRef | undefined = this.dialogService.open(DatailsSmartOltConfigurationsComponent, {
      header: `ONU ${sn}`,
      data: {
        sn: sn,
        contractID: contractID,
        companyOwner: companyOwner,
      },
      maximizable: true,
      width: ONU_DIALOG_WIDTH,
      height: ONU_DIALOG_HEIGHT,
      // sin relleno inferior: la botonera queda fija al pie del contenido
      contentStyle: { 'padding-bottom': '0' },
      breakpoints: ONU_DIALOG_BREAKPOINTS,
    });

    return new Promise<boolean>((resolve) => {
      ref?.onClose.subscribe((data) => {
        if (data?.delete) {
          resolve(true);
        }
      });
    });
  }

  /**
   * showGetFullStatus
   */
  public async showGetFullStatus(sn: string, companyOwner: number) {
    this.globalStateService.setShowLoading(true);
    try {
      const onuDetails = await this.getFullInfoStatusOnu(sn, companyOwner);
      this.dialogService.open(StatusInfoOnuComponent, {
        header: `Status ONU ${sn}`,
        data: { sn: sn, onuDetails: onuDetails.data },
        maximizable: true,
      });
      this.globalStateService.setShowLoading(false);
    } catch (error) {
      this.globalStateService.setShowLoading(false);
    }
  }

  /**
   * showGetRunningConfig
   */
  public async showGetRunningConfig(sn: string, companyOwner: number) {
    this.globalStateService.setShowLoading(true);
    this.getRunningConfig(sn, companyOwner)
      .then((response) => {
        const runningConfig = response.data;
        this.dialogService.open(ShowRunningConfigOnuComponent, {
          header: `Running config ONU ${sn}`,
          data: { sn: sn, runningConfig: runningConfig },
          maximizable: true,
          width: '70%',
          breakpoints: { '960px': '80vw', '640px': '90vw' },
        });
        this.globalStateService.setShowLoading(false);
      })
      .catch((error) => {
        this.globalStateService.setShowLoading(false);
        console.error(error);
      });
  }

  /**
   * getSwInfo
   */
  public async getSwInfo(sn: string, companyOwnerId: number) {
    return await firstValueFrom(
      this.http.get<any>(`${environment.apiUrl}/smart-olt/get-sw-info/${sn}`, {
        params: { companyOwnerId },
      }),
    );
  }

  /**
   * showswInfo
   */
  public showswInfo(sn: string, companyOwnerId: number) {
    this.globalStateService.setShowLoading(true);
    this.getSwInfo(sn, companyOwnerId)
      .then((response) => {
        const runningConfig: IRunningConfigOnu = {
          response_code: '',
          running_config: response.response,
          companyOwner: companyOwnerId,
        };

        this.dialogService.open(ShowRunningConfigOnuComponent, {
          header: `SW Info ${sn}`,
          data: { sn: sn, runningConfig: runningConfig },
          maximizable: true,
          width: '70%',
          height: '90vh',
          breakpoints: { '960px': '80vw', '640px': '90vw' },
        });
        this.globalStateService.setShowLoading(false);
      })
      .catch((error) => {
        this.globalStateService.setShowLoading(false);
        console.error(error);
      });
  }
  /**
   * getLiveStatus
   */
  public getLiveStatus(sn: string, companyOwnerId: number): Promise<ILiveStatusResponse> {
    const body = {
      sn: sn,
    };
    return firstValueFrom(
      this.http.post<ILiveStatusResponse>(`${environment.apiUrl}/smart-olt/live-onu`, body, {
        params: { companyOwnerId },
      }),
    );
  }

  /**
   * showLiveStatus
   */
  public async showLiveStatus(sn: string, companyOwner: number): Promise<void> {
    try {
      this.globalStateService.setShowLoading(true);
      this.dialogService.open(LiveStatuSmartOltModalComponent, {
        header: `Live Status ONU ${sn}`,
        data: { sn: sn, companyOwner: companyOwner },
        maximizable: true,
        // se abre desde el modal de la ONU: misma medida para no verse encima de él
        width: ONU_DIALOG_WIDTH,
        height: ONU_DIALOG_HEIGHT,
        breakpoints: ONU_DIALOG_BREAKPOINTS,
      });
      this.globalStateService.setShowLoading(false);
    } catch (error) {
      this.globalStateService.setShowLoading(false);
    }
  }
}
